import { useMemo, useState } from "react";
import type { CohortSizePoint } from "@/lib/api/sms-lifecycle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { CheckCircle2, Clock, Loader2, Send, Users } from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  useAuditTrace,
  useCohortStats,
  useCohortSizeHistory,
  useCohorts,
} from "@/lib/hooks/sms-lifecycle/use-sms-lifecycle";

const COHORT_COLORS = [
  "hsl(221, 83%, 53%)",
  "hsl(142, 71%, 45%)",
  "hsl(38, 92%, 50%)",
  "hsl(0, 84%, 60%)",
  "hsl(262, 83%, 58%)",
  "hsl(186, 72%, 44%)",
  "hsl(330, 81%, 60%)",
  "hsl(25, 95%, 53%)",
];

const CohortSizeCards = ({ data }: { data: CohortSizePoint[] }) => {
  const cohortCards = useMemo(() => {
    const byCohort = new Map<
      string,
      { name: string; points: { date: string; count: number }[] }
    >();

    for (const point of data) {
      if (!byCohort.has(point.cohortId)) {
        byCohort.set(point.cohortId, { name: point.cohortName, points: [] });
      }
      byCohort.get(point.cohortId)!.points.push({
        date: point.date,
        count: point.userCount,
      });
    }

    return [...byCohort.entries()].map(([id, { name, points }], i) => {
      const sorted = points.sort((a, b) => a.date.localeCompare(b.date));
      const current = sorted[sorted.length - 1]?.count ?? 0;
      const previous = sorted[sorted.length - 2]?.count ?? current;
      const delta = current - previous;
      const chartData = sorted.map((p) => ({
        date: new Date(p.date).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        }),
        count: p.count,
      }));

      return { id, name, current, delta, chartData, colorIndex: i };
    });
  }, [data]);

  if (cohortCards.length === 0) return null;

  return (
    <div className="space-y-2">
      <h3 className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
        <Users className="h-4 w-4" />
        Users per Cohort — Last 14 Days
      </h3>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {cohortCards.map((cohort) => {
          const color = COHORT_COLORS[cohort.colorIndex % COHORT_COLORS.length];
          return (
            <Card key={cohort.id}>
              <CardHeader className="pb-2">
                <CardDescription className="text-xs">{cohort.name}</CardDescription>
                <div className="flex items-baseline gap-2">
                  <CardTitle className="text-2xl">
                    {cohort.current.toLocaleString()}
                  </CardTitle>
                  {cohort.delta !== 0 && (
                    <span
                      className={`text-xs font-medium ${cohort.delta > 0 ? "text-green-600" : "text-red-500"}`}
                    >
                      {cohort.delta > 0 ? "+" : ""}
                      {cohort.delta}
                    </span>
                  )}
                </div>
              </CardHeader>
              <CardContent className="pb-4">
                <ResponsiveContainer width="100%" height={80}>
                  <AreaChart data={cohort.chartData}>
                    <defs>
                      <linearGradient
                        id={`fill-${cohort.id}`}
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop offset="0%" stopColor={color} stopOpacity={0.3} />
                        <stop offset="100%" stopColor={color} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="date" hide />
                    <YAxis hide domain={["dataMin - 1", "dataMax + 1"]} />
                    <Tooltip
                      contentStyle={{
                        borderRadius: "6px",
                        border: "1px solid hsl(var(--border))",
                        background: "hsl(var(--popover))",
                        color: "hsl(var(--popover-foreground))",
                        fontSize: "12px",
                      }}
                      formatter={(value) => [value ?? 0, "Users"]}
                    />
                    <Area
                      type="monotone"
                      dataKey="count"
                      stroke={color}
                      strokeWidth={2}
                      fill={`url(#fill-${cohort.id})`}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

type StatusFilter = "all" | "sent" | "pending";

export const ObservabilityTab = () => {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [cohortFilter, setCohortFilter] = useState<string>("");
  const [userIdFilter, setUserIdFilter] = useState<string>("");
  const [dateRange, setDateRange] = useState<"today" | "7d" | "30d" | "all">("today");
  const [page, setPage] = useState(1);

  const dateRangeToParams = (range: typeof dateRange) => {
    if (range === "all") return {};
    const now = new Date();
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    const start = new Date(end);
    if (range === "today") start.setDate(start.getDate() - 1);
    else if (range === "7d") start.setDate(start.getDate() - 7);
    else if (range === "30d") start.setDate(start.getDate() - 30);
    return {
      startDate: start.toISOString().split("T")[0],
      endDate: end.toISOString().split("T")[0],
    };
  };

  const dateParams = dateRangeToParams(dateRange);
  const dateRangeLabel =
    dateRange === "today" ? "Today" : dateRange === "7d" ? "Last 7 Days" : dateRange === "30d" ? "Last 30 Days" : "All Time";

  const { data: cohorts, isLoading: cohortsLoading } = useCohorts();
  const { data: cohortStats, isLoading: statsLoading } = useCohortStats(dateParams);
  const { data: cohortSizeHistory, isLoading: sizeHistoryLoading } =
    useCohortSizeHistory(14);

  const totalScheduled =
    cohortStats?.reduce((sum, s) => sum + s.totalScheduled, 0) ?? 0;
  const totalSent = cohortStats?.reduce((sum, s) => sum + s.totalSent, 0) ?? 0;
  const totalPending =
    cohortStats?.reduce((sum, s) => sum + s.totalPending, 0) ?? 0;

  const { data, isLoading } = useAuditTrace({
    status: statusFilter,
    cohortId: cohortFilter || undefined,
    userId: userIdFilter.trim() || undefined,
    ...dateParams,
    page,
    limit: 50,
  });

  const resetPage = () => setPage(1);

  const hasAuditFilters =
    cohortFilter || userIdFilter || statusFilter !== "all";

  const clearAuditFilters = () => {
    setStatusFilter("all");
    setCohortFilter("");
    setUserIdFilter("");
    setPage(1);
  };

  const statsMap = new Map(cohortStats?.map((s) => [s.cohortId, s]));

  return (
    <div className="space-y-6">
      {/* ── Date range presets ──────────────────────────────────────────── */}
      <div className="flex items-center gap-1.5">
        {([
          ["today", "Today"],
          ["7d", "Last 7 Days"],
          ["30d", "Last 30 Days"],
          ["all", "All Time"],
        ] as const).map(([value, label]) => (
          <Button
            key={value}
            variant={dateRange === value ? "default" : "outline"}
            size="sm"
            onClick={() => { setDateRange(value); resetPage(); }}
          >
            {label}
          </Button>
        ))}
      </div>

      {/* ── Global summary cards ─────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        {statsLoading ? (
          <>
            {Array.from({ length: 3 }).map((_, i) => (
              <Card key={i}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-9 w-20" />
                    </div>
                    <Skeleton className="h-8 w-8 rounded-full" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </>
        ) : (
          <>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Scheduled — {dateRangeLabel}</p>
                    <p className="text-3xl font-bold">
                      {totalScheduled.toLocaleString()}
                    </p>
                  </div>
                  <Send className="h-8 w-8 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Sent — {dateRangeLabel}</p>
                    <p className="text-3xl font-bold text-green-600">
                      {totalSent.toLocaleString()}
                    </p>
                  </div>
                  <CheckCircle2 className="h-8 w-8 text-green-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Pending — {dateRangeLabel}</p>
                    <p className="text-3xl font-bold text-yellow-600">
                      {totalPending.toLocaleString()}
                    </p>
                  </div>
                  <Clock className="h-8 w-8 text-yellow-600" />
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* ── Per-cohort stats ──────────────────────────────────────────── */}
      {cohortsLoading || statsLoading ? (
        <Card>
          <CardHeader>
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-4 w-64" />
          </CardHeader>
          <CardContent className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </CardContent>
        </Card>
      ) : cohorts && cohorts.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Stats by Cohort</CardTitle>
            <CardDescription>
              Per-cohort delivery metrics — click a row to filter everything
              below
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cohort</TableHead>
                  <TableHead className="text-right">Priority</TableHead>
                  <TableHead className="text-right">Active Users</TableHead>
                  <TableHead className="text-right">Scheduled{dateRange !== "all" ? ` (${dateRangeLabel})` : ""}</TableHead>
                  <TableHead className="text-right">Sent{dateRange !== "all" ? ` (${dateRangeLabel})` : ""}</TableHead>
                  <TableHead className="text-right">Pending{dateRange !== "all" ? ` (${dateRangeLabel})` : ""}</TableHead>
                  <TableHead className="text-right">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cohorts.map((cohort) => {
                  const stats = statsMap.get(cohort.id);
                  return (
                    <TableRow
                      key={cohort.id}
                      className={`cursor-pointer hover:bg-muted/50 ${cohort.id === cohortFilter ? "bg-muted/40" : ""}`}
                      onClick={() => {
                        setCohortFilter(
                          cohort.id === cohortFilter ? "" : cohort.id,
                        );
                        resetPage();
                      }}
                    >
                      <TableCell className="font-medium">
                        {cohort.name}
                        {cohort.id === cohortFilter && (
                          <Badge variant="outline" className="ml-2 text-xs">
                            filtered
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {cohort.priority}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Users className="h-3 w-3 text-muted-foreground" />
                          {stats?.activeUsers?.toLocaleString() ?? "—"}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {stats?.totalScheduled?.toLocaleString() ?? "—"}
                      </TableCell>
                      <TableCell className="text-right text-green-600">
                        {stats?.totalSent?.toLocaleString() ?? "—"}
                      </TableCell>
                      <TableCell className="text-right text-yellow-600">
                        {stats?.totalPending?.toLocaleString() ?? "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge
                          variant={cohort.enabled ? "default" : "secondary"}
                        >
                          {cohort.enabled ? "Active" : "Disabled"}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : null}

      {/* ── Cohort size over time ────────────────────────────────────── */}
      {sizeHistoryLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-4 w-48" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Card key={i}>
                <CardHeader className="pb-2">
                  <Skeleton className="h-3 w-24" />
                  <Skeleton className="h-8 w-16" />
                </CardHeader>
                <CardContent className="pb-4">
                  <Skeleton className="h-[80px] w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ) : cohortSizeHistory && cohortSizeHistory.length > 0 ? (
        <CohortSizeCards data={cohortSizeHistory} />
      ) : null}

      {/* ── Audit trace ──────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <CardTitle>Audit Trace</CardTitle>
              <CardDescription>
                {data?.total !== undefined
                  ? `${data.total.toLocaleString()} records matching current filters`
                  : "Scheduled and sent message log"}
              </CardDescription>
            </div>

            {/* Filter controls */}
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Status</Label>
                <Select
                  value={statusFilter}
                  onValueChange={(v) => {
                    setStatusFilter(v as StatusFilter);
                    resetPage();
                  }}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="sent">Sent</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Cohort</Label>
                <Select
                  value={cohortFilter || "_all"}
                  onValueChange={(v) => {
                    setCohortFilter(v === "_all" ? "" : v);
                    resetPage();
                  }}
                >
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="All cohorts" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">All cohorts</SelectItem>
                    {cohorts?.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label className="text-xs">User ID</Label>
                <Input
                  placeholder="Filter by user UUID"
                  value={userIdFilter}
                  onChange={(e) => {
                    setUserIdFilter(e.target.value);
                    resetPage();
                  }}
                  className="w-48 font-mono text-xs"
                />
              </div>

              {hasAuditFilters && (
                <Button variant="ghost" size="sm" onClick={clearAuditFilters}>
                  Clear filters
                </Button>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {isLoading ? (
            <div className="flex h-24 w-full items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User ID</TableHead>
                    <TableHead>Cohort</TableHead>
                    <TableHead>Message Preview</TableHead>
                    <TableHead>Scheduled</TableHead>
                    <TableHead>Sent</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-mono text-xs">
                        {item.user_id.slice(0, 8)}…
                      </TableCell>
                      <TableCell>{item.cohort_name ?? "—"}</TableCell>
                      <TableCell className="max-w-sm truncate text-sm">
                        {item.message}
                      </TableCell>
                      <TableCell className="text-nowrap text-sm">
                        {new Date(item.scheduled_at).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-nowrap text-sm">
                        {item.sent_at
                          ? new Date(item.sent_at).toLocaleString()
                          : "—"}
                      </TableCell>
                      <TableCell>
                        {item.sent_at ? (
                          <Badge variant="default">Sent</Badge>
                        ) : (
                          <Badge variant="secondary">Pending</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}

                  {data?.items.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="py-8 text-center text-muted-foreground"
                      >
                        No records match the current filters.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              {data && data.totalPages > 1 && (
                <div className="mt-4 flex items-center justify-between">
                  <Button
                    variant="outline"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {page} of {data.totalPages}
                  </span>
                  <Button
                    variant="outline"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page === data.totalPages}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
